import React, { useRef } from "react";
import { Box, Skeleton, Stack, Typography } from "@mui/material";
import { ResponsiveSankey } from "@nivo/sankey";
import styled from "@emotion/styled";
import LegendSwatch from "@hyro-components/hyro-pie-chart/LegendSwatch";
import Tooltip from "@hyro-components/Tooltip";
import HyroChartTooltip from "@hyro-components/hyro-chart-tooltip/HyroChartTooltip";
import { hasTooltipLinks } from "@hyro-components/hyro-chart-tooltip/HyroChartTooltip.utils";
import QuestionIcon from "src/icons/insights/QuestionIcon";
import { commaNotation } from "@utils";
import {
  SANKEY_CARD_DIM_OPACITY,
  SANKEY_CARD_GAP,
  SANKEY_CARD_HEIGHT,
  SANKEY_CARD_OVERHANG,
  SANKEY_CARD_SHADOW,
  SANKEY_COLORS,
  SANKEY_COMPACT_LEGEND_ICON_SIZE,
  SANKEY_COMPACT_LEGEND_SPACING,
  SANKEY_DEFAULT_HEIGHT,
  SANKEY_HOVER_TRANSITION,
  SANKEY_LEGEND_ICON_SIZE,
  SANKEY_LEGEND_SPACING,
  SANKEY_LINK_OPACITY,
  SANKEY_MIN_BEND_THICKNESS,
  SANKEY_MIN_CHART_WIDTH,
  SANKEY_MIN_NODE_HEIGHT_RATIO,
  SANKEY_NODE_BORDER_RADIUS,
  SANKEY_NODE_OPACITY,
  SANKEY_NODE_SPACING,
  SANKEY_NODE_THICKNESS,
  SANKEY_RIGHT_MARGIN,
  SANKEY_TEST_IDS,
  SANKEY_TOOLTIP_BOUNDARY_PADDING,
  SANKEY_TOP_MARGIN,
  sankeyCardTestId,
  sankeyLinkTestId,
  sankeyNodeTestId
} from "./HyroSankey.consts";

const CARD_ANCHOR_WIDTH = 300;
const MIN_CARD_WIDTH = 90;
const LINK_BEND_RATIO = 0.12;

// every ribbon renders as a band of at least SANKEY_MIN_BEND_THICKNESS (screen px,
// so it survives the node-layout scale) to keep thin flows legible; zero-value links
// keep their (hairline) true thickness.
const effectiveThickness = (link, scale) =>
  link.value > 0
    ? Math.max(link.thickness * scale, SANKEY_MIN_BEND_THICKNESS)
    : Math.max(1, link.thickness * scale);

const linkKey = (link) => `${link.source.id}.${link.target.id}`;

// d3-sankey stacks link endpoints by their true thickness, so once every ribbon is
// clamped to a >=SANKEY_MIN_BEND_THICKNESS band the thin ones overlap. Re-derive each
// endpoint's centerline from the clamped band, centered on the node's re-stacked rect.
// Outgoing endpoints follow the re-stacked targets top-down; incoming endpoints then follow
// where each ribbon leaves its source, so ribbons entering a node from several columns do
// not cross each other. A stack is centered on the node's base height (its data height
// or the minimum) so a thin node keeps its ribbon in the middle; a stack taller than that
// is top-aligned, which keeps the top edge of the first ribbon flush with both nodes when
// the rect was grown downward to cover it (buildNodeLayout).
const buildLinkLayout = (links, layout) => {
  const y0 = {};
  const y1 = {};
  const stack = (nodeId, group, order, assign) => {
    const rect = layout[nodeId];
    if (!rect) return;
    const sorted = [...group].sort((a, b) => order(a) - order(b));
    const total = sorted.reduce((sum, link) => sum + effectiveThickness(link, rect.scale), 0);
    let cursor = rect.y + Math.max(0, (rect.baseHeight - total) / 2);
    sorted.forEach((link) => {
      const band = effectiveThickness(link, rect.scale);
      assign(linkKey(link), cursor + (band / 2));
      cursor += band;
    });
  };
  const bySource = {};
  const byTarget = {};
  links.forEach((link) => {
    (bySource[link.source.id] = bySource[link.source.id] ?? []).push(link);
    (byTarget[link.target.id] = byTarget[link.target.id] ?? []).push(link);
  });
  Object.entries(bySource).forEach(([id, group]) =>
    stack(id, group, (link) => layout[link.target.id]?.y ?? link.pos0, (key, y) => { y0[key] = y; }));
  Object.entries(byTarget).forEach(([id, group]) =>
    stack(id, group, (link) => y0[linkKey(link)] ?? link.pos1, (key, y) => { y1[key] = y; }));
  return { y0, y1 };
};

// same ribbon shape nivo draws (flat ends, s-curve middle); a uniform >=6px band
// end-to-end, with endpoints re-stacked by buildLinkLayout so the bands tile cleanly.
const linkPath = (link, layout, linkLayout) => {
  const scale = layout[link.source.id]?.scale ?? 1;
  const half = effectiveThickness(link, scale) / 2;
  const key = linkKey(link);
  const y0 = linkLayout.y0[key];
  const y1 = linkLayout.y1[key];
  const x0 = link.source.x1;
  const x1 = link.target.x0;
  const bend = LINK_BEND_RATIO * (x1 - x0);
  const mid = (x0 + x1) / 2;
  return [
    `M ${x0} ${y0 - half}`,
    `L ${x0 + bend} ${y0 - half}`,
    `C ${mid} ${y0 - half} ${mid} ${y1 - half} ${x1 - bend} ${y1 - half}`,
    `L ${x1} ${y1 - half}`,
    `L ${x1} ${y1 + half}`,
    `L ${x1 - bend} ${y1 + half}`,
    `C ${mid} ${y1 + half} ${mid} ${y0 + half} ${x0 + bend} ${y0 + half}`,
    `L ${x0} ${y0 + half}`,
    "Z"
  ].join(" ");
};

const LinksLayer = ({ nodes, links, height: chartHeight, margin, currentNode, currentLink, isCurrentLink }) => {
  const innerHeight = chartHeight - margin.top - margin.bottom;
  const layout = buildNodeLayout(nodes, links, innerHeight);
  const linkLayout = buildLinkLayout(links, layout);
  const hasFocus = Boolean(currentNode || currentLink);

  const linkOpacity = (link) => {
    if (!hasFocus) return SANKEY_LINK_OPACITY.DEFAULT;
    return isCurrentLink(link) ? SANKEY_LINK_OPACITY.HOVER : SANKEY_LINK_OPACITY.DIM;
  };

  return (
    <g data-testid={SANKEY_TEST_IDS.LINKS}>
      {links.map((link) => (
        <path
          key={linkKey(link)}
          data-testid={sankeyLinkTestId(link.source.id, link.target.id)}
          d={linkPath(link, layout, linkLayout)}
          fill={link.target.color}
          style={{ opacity: linkOpacity(link), transition: SANKEY_HOVER_TRANSITION }}
        />
      ))}
    </g>
  );
};

// percent is out of the node's parents by default; with percentBaseId every node is out of that one node
const buildPercentById = (nodes, links, percentBaseId) => {
  const incoming = {};
  const outgoing = {};
  const parentsById = {};
  links.forEach(({ source, target, value }) => {
    incoming[target] = (incoming[target] ?? 0) + value;
    outgoing[source] = (outgoing[source] ?? 0) + value;
    parentsById[target] = parentsById[target] ?? new Set();
    parentsById[target].add(source);
  });
  const valueOf = (id) => incoming[id] ?? outgoing[id] ?? 0;
  const percentOf = (id, base) => (base ? Math.round((valueOf(id) / base) * 100) : 0);
  if (percentBaseId) {
    const base = valueOf(percentBaseId);
    return Object.fromEntries(nodes.map(({ id }) => [id, percentOf(id, base)]));
  }
  return Object.fromEntries(nodes.map(({ id }) => {
    const parents = parentsById[id];
    if (!parents) return [id, 100];
    return [id, percentOf(id, [...parents].reduce((sum, parentId) => sum + valueOf(parentId), 0))];
  }));
};

const SCALE_FIT_PASSES = 8;

// Re-stack each column top-down with a constant SANKEY_NODE_SPACING gap between node
// rects. The gap is held fixed (so the above-node cards always fit); when the tallest
// column would overflow the chart, node heights are scaled down by a single global
// factor so every column fits without cutting nodes off. `scale` is stored so the link
// ribbons (thickness + endpoint positions) shrink by the same factor and stay attached.
// A node is never shorter than the stack of clamped ribbon bands entering or leaving it,
// so thin ribbons widened to SANKEY_MIN_BEND_THICKNESS cannot spill past the rect; the
// growth is added below the node's base height. That floor does not scale, so the fit is
// found by re-measuring a few times.
const buildNodeLayout = (nodes, links, innerHeight) => {
  const minHeight = SANKEY_MIN_NODE_HEIGHT_RATIO * innerHeight;
  const incoming = {};
  const outgoing = {};
  links.forEach((link) => {
    (outgoing[link.source.id] = outgoing[link.source.id] ?? []).push(link);
    (incoming[link.target.id] = incoming[link.target.id] ?? []).push(link);
  });
  const stackHeight = (group = [], scale) => group.reduce((sum, link) => sum + effectiveThickness(link, scale), 0);
  const baseHeight = (node, scale) => Math.max(node.height, minHeight) * scale;
  const nodeHeight = (node, scale) => Math.max(
    baseHeight(node, scale),
    stackHeight(incoming[node.id], scale),
    stackHeight(outgoing[node.id], scale)
  );

  const columns = {};
  nodes.forEach((node) => {
    const column = Math.round(node.x);
    (columns[column] = columns[column] ?? []).push(node);
  });
  const columnGroups = Object.values(columns).map((columnNodes) => [...columnNodes].sort((a, b) => a.y - b.y));
  const columnHeight = (sorted, scale) =>
    sorted.reduce((sum, node) => sum + nodeHeight(node, scale), 0) + (SANKEY_NODE_SPACING * (sorted.length - 1));

  let scale = 1;
  for (let pass = 0; pass < SCALE_FIT_PASSES; pass += 1) {
    const tallest = columnGroups.reduce((worst, sorted) => {
      const total = columnHeight(sorted, scale);
      return total > worst.total ? { total, sorted } : worst;
    }, { total: 0, sorted: [] });
    if (tallest.total <= innerHeight) break;
    const gaps = SANKEY_NODE_SPACING * (tallest.sorted.length - 1);
    scale *= Math.max(innerHeight - gaps, 0) / (tallest.total - gaps);
  }

  const layout = {};
  columnGroups.forEach((sorted) => {
    let top = 0;
    sorted.forEach((node) => {
      const height = nodeHeight(node, scale);
      layout[node.id] = { y: top, height, baseHeight: baseHeight(node, scale), scale };
      top += height + SANKEY_NODE_SPACING;
    });
  });
  return layout;
};

const HyroSankey = ({
  data = { nodes: [], links: [] },
  height = SANKEY_DEFAULT_HEIGHT,
  loading = false,
  legendGroups,
  formatValue = commaNotation,
  onNodeClick,
  renderTooltip,
  tooltipBoundaryRef,
  percentBaseId,
  compactLegend = false
}) => {
  const legendSpacing = compactLegend ? SANKEY_COMPACT_LEGEND_SPACING : SANKEY_LEGEND_SPACING;
  const legendIconSize = compactLegend ? SANKEY_COMPACT_LEGEND_ICON_SIZE : SANKEY_LEGEND_ICON_SIZE;
  const hoverTooltipRef = useRef(null);
  const showHoverTooltip = (node, event) => hoverTooltipRef.current?.show(
    node,
    new DOMRect(event.clientX, event.clientY, 0, 0),
    { interactive: hasTooltipLinks(node.extra), boundary: tooltipBoundaryRef?.current }
  );

  // with a boundary element the tooltip stays inside it: it flips to the opposite side when
  // the cursor is near the top/bottom edge, and slides along the cross axis near left/right.
  // With no boundary popper falls back to clippingParents, so positioning is unchanged.
  const tooltipModifiers = ({ boundary, interactive }) => [
    { name: "offset", options: { offset: [0, interactive ? 0 : SANKEY_CARD_GAP] } },
    { name: "flip", options: { boundary, padding: SANKEY_TOOLTIP_BOUNDARY_PADDING } },
    { name: "preventOverflow", options: { boundary, padding: SANKEY_TOOLTIP_BOUNDARY_PADDING, altAxis: true, tether: false } }
  ];

  if (loading) {
    return (
      <Box sx={{ height, width: "100%" }}>
        <Skeleton variant="rectangular" width="100%" height="100%" sx={{ borderRadius: 1 }} />
      </Box>
    );
  }

  const hasOutgoing = new Set(data.links.map(({ source }) => source));
  const chartNodes = data.nodes.map((node) => ({
    ...node,
    label: node.label ?? node.id,
    color: node.color ?? (hasOutgoing.has(node.id) ? SANKEY_COLORS.PRIMARY : SANKEY_COLORS.LEAF)
  }));
  const percentById = buildPercentById(chartNodes, data.links, percentBaseId);

  const nodeSummary = (node) => ({
    id: node.id,
    label: node.label,
    value: node.value,
    percent: percentById[node.id],
    color: node.color,
    extra: node.extra
  });

  const cardText = (node) => `${formatValue(node.value)} (${percentById[node.id]}%)`;

  const hoverTooltipContent = (node) => (
    <SankeyCard>
      {renderTooltip
        ? renderTooltip(nodeSummary(node))
        : (
          <>
            <LegendSwatch color={node.color} />
            <Typography variant="tooltip" sx={{ whiteSpace: "nowrap" }}>{node.label}</Typography>
            <Typography color="text.700" variant="tooltip" sx={{ whiteSpace: "nowrap" }}>{cardText(node)}</Typography>
          </>
        )}
    </SankeyCard>
  );

  const NodesLayer = ({ nodes, links, height: chartHeight, margin, currentNode, currentLink, isCurrentNode, setCurrentNode }) => {
    const innerHeight = chartHeight - margin.top - margin.bottom;
    const layout = buildNodeLayout(nodes, links, innerHeight);
    const hasFocus = Boolean(currentNode || currentLink);

    const nodeOpacity = (node) => {
      if (!hasFocus) return SANKEY_NODE_OPACITY.DEFAULT;
      return isCurrentNode(node) ? SANKEY_NODE_OPACITY.HOVER : SANKEY_NODE_OPACITY.DIM;
    };

    return (
      <g data-testid={SANKEY_TEST_IDS.NODES}>
        {nodes.map((node) => {
          const { y, height: displayHeight } = layout[node.id];
          return (
            <rect
              key={node.id}
              data-testid={sankeyNodeTestId(node.id)}
              x={node.x}
              y={y}
              width={node.width}
              height={displayHeight}
              rx={SANKEY_NODE_BORDER_RADIUS}
              ry={SANKEY_NODE_BORDER_RADIUS}
              fill={node.color}
              style={{ opacity: nodeOpacity(node), transition: SANKEY_HOVER_TRANSITION, cursor: "pointer" }}
              onMouseEnter={(event) => {
                setCurrentNode(node);
                showHoverTooltip(node, event);
              }}
              onMouseMove={(event) => showHoverTooltip(node, event)}
              onMouseLeave={() => {
                setCurrentNode(null);
                hoverTooltipRef.current?.hide();
              }}
              onClick={(event) => onNodeClick?.(nodeSummary(node), event)}
            />
          );
        })}
      </g>
    );
  };

  const CardsLayer = ({ nodes, links, width: chartWidth, height: chartHeight, margin, currentNode, currentLink, isCurrentNode }) => {
    const innerHeight = chartHeight - margin.top - margin.bottom;
    const layout = buildNodeLayout(nodes, links, innerHeight);
    const hasFocus = Boolean(currentNode || currentLink);
    const columnXs = [...new Set(nodes.map((node) => Math.round(node.x)))].sort((a, b) => a - b);

    const maxCardWidth = (node) => {
      const nextColumnX = columnXs.find((x) => x > Math.round(node.x));
      const limit = (nextColumnX ?? chartWidth - margin.left) - node.x - SANKEY_CARD_GAP + SANKEY_CARD_OVERHANG;
      return Math.max(limit, MIN_CARD_WIDTH);
    };

    return (
      <g data-testid={SANKEY_TEST_IDS.CARDS}>
        {nodes.map((node) => {
          const { y } = layout[node.id];
          const opacity = hasFocus && !isCurrentNode(node) ? SANKEY_CARD_DIM_OPACITY : 1;
          return (
            <foreignObject
              key={node.id}
              x={node.x}
              y={y - SANKEY_CARD_GAP - SANKEY_CARD_HEIGHT}
              width={CARD_ANCHOR_WIDTH}
              height={SANKEY_CARD_HEIGHT}
              style={{ overflow: "visible", pointerEvents: "none", opacity, transition: SANKEY_HOVER_TRANSITION }}
            >
              <Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "flex-end", height: "100%" }}>
                <SankeyCard data-testid={sankeyCardTestId(node.id)} style={{ maxWidth: maxCardWidth(node) }}>
                  <LegendSwatch color={node.color} />
                  {/* the label keeps its full width and the value wraps to a second row when the
                      column is too narrow for both, so long labels stay readable at any chart width */}
                  <Typography variant="tooltip" sx={{ whiteSpace: "nowrap" }}>{node.label}</Typography>
                  <Typography color="text.700" variant="tooltip" sx={{ whiteSpace: "nowrap" }}>{cardText(node)}</Typography>
                </SankeyCard>
              </Box>
            </foreignObject>
          );
        })}
      </g>
    );
  };

  return (
    <Stack sx={{ width: "100%" }} gap={3} data-testid={SANKEY_TEST_IDS.ROOT}>
      {/* overflowY must be explicit: overflowX:auto forces the computed overflow-y to auto,
          and the svg baseline gap adds a few stray px that would otherwise show a vertical scrollbar */}
      <Box sx={{ width: "100%", overflowX: "auto", overflowY: "hidden" }}>
        <Box sx={{ height, width: "100%", minWidth: SANKEY_MIN_CHART_WIDTH, "& svg": { display: "block" } }}>
          <ResponsiveSankey
            data={{ nodes: chartNodes, links: data.links }}
            margin={{ top: SANKEY_TOP_MARGIN, right: SANKEY_RIGHT_MARGIN, bottom: 0, left: 8 }}
            align="start"
            sort="input"
            colors={(node) => node.color}
            label={(node) => node.label}
            nodeThickness={SANKEY_NODE_THICKNESS}
            nodeSpacing={SANKEY_NODE_SPACING}
            nodeBorderWidth={0}
            enableLabels={false}
            layers={[LinksLayer, NodesLayer, CardsLayer]}
          />
        </Box>
      </Box>
      <HyroChartTooltip
        ref={hoverTooltipRef}
        renderContent={hoverTooltipContent}
        gap={SANKEY_CARD_GAP}
        modifiers={tooltipModifiers}
      />
      {legendGroups && (
        <LegendBar spacing={legendSpacing} data-testid={SANKEY_TEST_IDS.LEGEND}>
          {legendGroups.map((group, groupIndex) => (
            <React.Fragment key={group.title ?? groupIndex}>
              {groupIndex > 0 && <LegendDivider data-testid={SANKEY_TEST_IDS.LEGEND_DIVIDER} />}
              <LegendGroup spacing={legendSpacing}>
                {group.title && <Typography variant="tooltip" color="text.600" sx={{ whiteSpace: "nowrap" }}>{group.title}</Typography>}
                {group.items.map((item) => (
                  <LegendItem key={item.label} spacing={legendSpacing}>
                    <LegendSwatch color={item.color} />
                    <LegendLabel spacing={legendSpacing}>
                      <Typography variant="tooltip" color="text.700" sx={{ whiteSpace: "nowrap" }}>{item.label}</Typography>
                      {item.tooltip && (
                        <Tooltip variant="light" title={item.tooltip} popperStyle={{ marginBottom: "8px", maxWidth: 280 }}>
                          <Box sx={{ width: legendIconSize, height: legendIconSize, display: "flex" }}>
                            <QuestionIcon size={legendIconSize} />
                          </Box>
                        </Tooltip>
                      )}
                    </LegendLabel>
                  </LegendItem>
                ))}
              </LegendGroup>
            </React.Fragment>
          ))}
        </LegendBar>
      )}
    </Stack>
  );
};

const SankeyCard = styled("div")(({ theme }) => ({
  background: theme.palette.system[0],
  padding: theme.spacing(1.5, 1.75),
  borderRadius: theme.spacing(0.5),
  boxShadow: SANKEY_CARD_SHADOW,
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  flexWrap: "wrap",
  gap: theme.spacing(0, 1),
  ...theme.typography.tooltip,
  color: theme.palette.text[800]
}));

const legendStyled = (tag) => styled(tag, { shouldForwardProp: (prop) => prop !== "spacing" });

const LegendBar = legendStyled("div")(({ theme, spacing }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: spacing.group,
  width: "100%",
  paddingTop: theme.spacing(3),
  borderTop: `1px solid ${theme.palette.system[200]}`,
  flexWrap: "wrap"
}));

const LegendGroup = legendStyled("div")(({ spacing }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: spacing.item
}));

const LegendItem = legendStyled("div")(({ spacing }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: spacing.swatch
}));

const LegendLabel = legendStyled("div")(({ spacing }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: spacing.icon
}));

const LegendDivider = styled("div")(({ theme }) => ({
  width: 1,
  height: 18,
  backgroundColor: theme.palette.system[200],
  flexShrink: 0
}));

export default HyroSankey;
